import type { ProjectAccessRole } from '../../models/projectAccess';
import type { ExportActionId } from '../../models/exportActions';
import type { ReportCatalogId } from '../../models/reportCatalog';

/**
 * Copy for the public landing page.
 *
 * Every claim here describes something the editor does today. Counts and
 * labels that already live in a product registry (views, reports, roles,
 * export formats, strip colours, category colours) are read from that
 * registry in the components instead of being repeated here.
 */

export const SCRIPT_IMPORT_FORMATS = ['PDF', 'FDX', 'Fountain', 'DOCX', 'RTF', 'TXT'] as const;

export const FEATURE_PAGES = [
  { title: 'AI Breakdown', path: '/features/script-analysis' },
  { title: 'Scheduling', path: '/features/scheduling' },
  { title: 'Budget Roadmap', path: '/features/budget' },
  { title: 'Collaboration', path: '/features/collaboration' },
  { title: 'Exports', path: '/features/export' },
  { title: 'Security', path: '/features/security' },
] as const;

export const WORKFLOW_STEPS = [
  {
    label: 'Import',
    title: 'Bring in the script',
    body: 'Upload a PDF, Final Draft, Fountain, Word, RTF or text file. Pick AI to extract the elements for you, or Manual to tag them yourself.',
  },
  {
    label: 'Break down',
    title: 'Check every scene',
    body: 'Correct headings, page counts, synopses and tags. Merge or split scenes, group sequences and add your own categories.',
  },
  {
    label: 'Schedule',
    title: 'Board the shoot',
    body: 'Drag strips into shooting order, add day breaks and banners, review conflicts, and save versions you can go back to.',
  },
  {
    label: 'Distribute',
    title: 'Hand out the paperwork',
    body: 'Call sheets, sides, DOOD and reports come from the same schedule, ready as PDF, spreadsheet or print.',
  },
] as const;

export const BREAKDOWN_POINTS = [
  'Read and edit the screenplay with proper scene heading, action, character and dialogue lines',
  'Add, edit, merge and hide tags, and create categories with your own colours',
  'Merge or split scenes and group them into sequences',
  'Write a synopsis for the whole script and for each scene',
  'Export the breakdown as PDF, XLSX or CSV, and the script as Final Draft FDX',
] as const;

export const SCHEDULE_POINTS = [
  'Drag-and-drop strips, day breaks and banners',
  'Place day breaks by pages, hours or scene count, around days off and holidays',
  'Sort on several keys, filter, and park scenes in the Scriptyard or Boneyard',
  'Assign Main, 2nd, VFX or Splinter units',
  'Catch missing locations, units and durations, and days over 12 hours',
  'Save versions and restore them, undo and redo with Ctrl/⌘ + Z',
  'Move scenes on a month or week calendar, or swap whole shooting days',
] as const;

export const ACCESS_POINTS = [
  'Invite people with single-use links that expire and only work for the invited email',
  'Projects shared with you appear under Shared with me',
  'Saved changes are checked against newer edits, so nobody overwrites a teammate',
  'Restore a deleted project from Recently Deleted until it is purged',
] as const;

export type RoleCapability = 'breakdown' | 'planning' | 'access' | 'export';

export const ROLE_CAPABILITY_LABELS: Readonly<Record<RoleCapability, string>> = {
  breakdown: 'Edit the breakdown',
  planning: 'Edit schedule & call sheets',
  access: 'Manage access',
  export: 'View & export',
};

/** Checked against deriveProjectCapabilities in the landing tests. */
export const ROLE_CAPABILITIES: Readonly<Record<ProjectAccessRole, readonly RoleCapability[]>> = {
  owner: ['breakdown', 'planning', 'access', 'export'],
  ad_editor: ['planning', 'export'],
  viewer: ['export'],
};

/** What each catalog report covers, in the landing page's words. */
export const REPORT_SUMMARIES: Readonly<Record<ReportCatalogId, string>> = {
  'breakdown-summary': 'Scene totals, timings, categories and a cross-reference of every element.',
  schedule: 'Shooting order, shooting days, locations, cast and timing.',
  dood: 'Start, work, hold and finish days for cast, extras or every element.',
  vfx: 'VFX scenes and elements, grouped by type and rated by complexity.',
};

/** Extra outputs shown next to the report catalog in the exports table. */
export const EXTRA_OUTPUTS: readonly {
  label: string;
  description: string;
  actionIds: readonly ExportActionId[];
}[] = [
  {
    label: 'Call Sheets',
    description: 'One per shooting day, A4 or Letter, with section visibility and two templates.',
    actionIds: ['call-sheet-pdf', 'call-sheet-print'],
  },
  {
    label: 'Sides',
    description: 'The day’s scenes with highlights, notes and crossed-out lines.',
    actionIds: ['sides-print'],
  },
  {
    label: 'Screenplay & synopsis',
    description: 'The script as Final Draft and the synopsis as plain text.',
    actionIds: ['script-fdx', 'synopsis-txt'],
  },
];

export const MOOD_LOOK_PRESETS = [
  'Noir', 'Golden hour', 'Monsoon', 'Neon night', 'Muted thriller', 'Pastel romance', '70s film', 'Festival colour',
] as const;

export const STOCK_SOURCES = ['Pexels', 'Unsplash', 'Openverse', 'Pixabay'] as const;

export type AccessOption = {
  name: string;
  priceLabel: string;
  description: string;
  features: readonly string[];
  badge?: string;
  action: { label: string; href: string; internal: boolean };
};

export const ACCESS_OPTIONS: readonly AccessOption[] = [
  {
    name: 'Free',
    priceLabel: '$0',
    description: 'Available for self-service signup',
    badge: 'Available now',
    features: [
      'Create and manage production projects',
      '5 AI script-breakdown projects, included once',
      'Manual breakdown and scene editing',
      'Scheduling, reports, and production exports',
      'Role-based project sharing',
    ],
    action: { label: 'Create Free Account', href: '/try-free', internal: true },
  },
  {
    name: 'Paid AI Access',
    priceLabel: 'Contact us',
    description: 'Managed activation for AI-assisted workflows',
    features: [
      'Full-project AI script breakdown',
      'AI-assisted scene analysis',
      'Core planning and export tools',
      'Availability and limits confirmed before activation',
    ],
    action: {
      label: 'Request AI Access',
      href: 'mailto:sales@ezyprep.com?subject=EzyPrep%20Paid%20AI%20Access',
      internal: false,
    },
  },
  {
    name: 'Enterprise',
    priceLabel: 'Contact us',
    description: 'Requirement-led access for organizations',
    features: [
      'Production and security requirement review',
      'Account and rollout planning',
      'Commercial terms confirmed in writing',
      'Deployment model confirmed before commitment',
    ],
    action: {
      label: 'Discuss Requirements',
      href: 'mailto:sales@ezyprep.com?subject=EzyPrep%20Enterprise%20Requirements',
      internal: false,
    },
  },
];

export const FAQ_ITEMS: readonly { question: string; answer: string; link?: { label: string; to: string } }[] = [
  {
    question: 'Which script formats can I import?',
    answer: 'PDF, Final Draft (.fdx), Fountain, Word (.docx), RTF and plain text.',
  },
  {
    question: 'Do I have to use AI?',
    answer: 'No. Choose Manual when you create a project and tag the elements yourself. Manual breakdown, scheduling, reports and exports are part of the free account.',
  },
  {
    question: 'What does the free account include?',
    answer: 'Five AI breakdown projects, once per account. Deleting a project does not give its slot back. Manual breakdown, scheduling, reports, exports and role-based sharing are included.',
  },
  {
    question: 'How accurate is the AI breakdown?',
    answer: 'Treat it as a first draft. Every scene and tag stays editable, and you should review the breakdown before you schedule from it.',
  },
  {
    question: 'Are the reference images AI-generated?',
    answer: 'No. References are real photographs from Pexels, Unsplash, Openverse and Pixabay, or your own uploads. AI images only appear in storyboards and mood boards, when you ask for them.',
  },
  {
    question: 'Can I email call sheets from EzyPrep?',
    answer: 'Not yet. Export them as PDF or print them, then send them the way you do today.',
  },
  {
    question: 'Is there a budgeting module?',
    answer: 'Not today. Breakdown, stripboard, DOOD and VFX reports export to spreadsheets you can bring into your budgeting tool.',
    link: { label: 'Read the budgeting roadmap', to: '/features/budget' },
  },
  {
    question: 'How do I get more AI breakdowns?',
    answer: 'Self-service billing is not available yet. Email sales@ezyprep.com and we will confirm availability and limits before activating paid AI access.',
  },
];
